import { lazyRouteComponent, useLocation, useMatches } from '@tanstack/react-router';
import type { PropsWithChildren } from 'react';
import { useIsMember } from 'src/auth';
import { useAiV2Enabled } from 'src/hooks/useAiV2Enabled';
import { parseChatThreadId } from 'src/lib/ai/parseChatThreadId';
import { isChatHostPath } from './chatHostPaths';
import { createRouteComponentWithProps } from './routeComponent';

const ChatPage = lazyRouteComponent(() => import('src/pages/ChatPage/ChatPage'));
function useChatPageProps() {
  const pathname = useLocation({ select: (location) => location.pathname });
  const threadId = useMatches({
    select: (matches) => {
      const match = matches.at(-1);
      const params = match?.params;
      const search = match?.search;
      const segment =
        params && 'threadId' in params && typeof params.threadId === 'string' ? params.threadId : undefined;
      const query = search && 'threadId' in search && typeof search.threadId === 'string' ? search.threadId : undefined;
      return pathname.startsWith('/ai-inbox') ? (segment ?? query) : segment;
    },
  });
  const isInbox = pathname.startsWith('/ai-inbox');
  const numericId = isInbox ? parseChatThreadId(threadId?.replace(/^0+(?=\d)/, '')) : null;
  const runtimeThreadId = numericId === null ? threadId : String(numericId);
  return { threadId: runtimeThreadId, navigation: isInbox ? ('inbox' as const) : ('chat' as const) };
}
const HostedChatPage = createRouteComponentWithProps(ChatPage, useChatPageProps);

export function ChatRouteHost({ children }: PropsWithChildren) {
  const enabled = useAiV2Enabled();
  const isMember = useIsMember();
  const pathname = useLocation({ select: (location) => location.pathname });
  return enabled && isMember && isChatHostPath(pathname) ? <HostedChatPage /> : children;
}
